/**
 * Metric geometry of the Poincaré disk (curvature −1):  ds = 2|dz| / (1 − |z|²).
 */
import { type Complex, abs, arg, fromPolar } from './complex';
import { apply, inverse, translation } from './mobius';

/**
 * Hyperbolic distance d(z, w) = 2 artanh |z − w| / |1 − z̄w|.
 * Written via the identity 1 − δ² = (1 − |z|²)(1 − |w|²) / |1 − z̄w|² so that it stays accurate
 * when both points approach the boundary.
 */
export function distance(z: Complex, w: Complex): number {
  const dr = z.re - w.re;
  const di = z.im - w.im;
  const num = dr * dr + di * di;
  // 1 − z̄w
  const er = 1 - (z.re * w.re + z.im * w.im);
  const ei = -(z.re * w.im - z.im * w.re);
  const den = er * er + ei * ei;
  const delta = Math.sqrt(num / den);
  const oneMinusZ2 = (1 - Math.hypot(z.re, z.im)) * (1 + Math.hypot(z.re, z.im));
  const oneMinusW2 = (1 - Math.hypot(w.re, w.im)) * (1 + Math.hypot(w.re, w.im));
  const oneMinusDelta2 = (oneMinusZ2 * oneMinusW2) / den;
  return Math.log(((1 + delta) * (1 + delta)) / oneMinusDelta2);
}

/** Distance from the origin to a point at Euclidean radius r: 2 artanh r = ln((1+r)/(1−r)). */
export const distanceFromOrigin = (r: number): number => Math.log((1 + r) / (1 - r));

/** Point at fraction t ∈ [0, 1] along the geodesic segment from a to b (by arclength). */
export function geodesicPoint(a: Complex, b: Complex, t: number): Complex {
  const ta = translation(a);
  const w = apply(inverse(ta), b);
  const r = abs(w);
  if (r === 0) return { ...a };
  const d = distanceFromOrigin(r);
  const rt = Math.tanh((t * d) / 2);
  return apply(ta, { re: (w.re / r) * rt, im: (w.im / r) * rt });
}

/** n + 1 points sampled evenly (by arclength) along the geodesic from a to b. */
export function sampleGeodesic(a: Complex, b: Complex, n: number): Complex[] {
  const ta = translation(a);
  const w = apply(inverse(ta), b);
  const r = abs(w);
  const pts: Complex[] = [];
  if (r === 0) {
    for (let i = 0; i <= n; i++) pts.push({ ...a });
    return pts;
  }
  const d = distanceFromOrigin(r);
  for (let i = 0; i <= n; i++) {
    const rt = Math.tanh(((i / n) * d) / 2);
    pts.push(apply(ta, { re: (w.re / r) * rt, im: (w.im / r) * rt }));
  }
  return pts;
}

/** The two ideal (boundary) endpoints of the complete geodesic through a and b. */
export function idealEndpoints(a: Complex, b: Complex): [Complex, Complex] {
  const ta = translation(a);
  const w = apply(inverse(ta), b);
  const r = abs(w) || 1;
  const u = { re: w.re / r, im: w.im / r };
  return [apply(ta, { re: -u.re, im: -u.im }), apply(ta, u)];
}

/**
 * Samples the complete geodesic through a and b out to hyperbolic distance `reach` beyond each
 * point (the true line is infinitely long; this is the part that is visible).
 */
export function sampleFullGeodesic(a: Complex, b: Complex, reach: number, n: number): Complex[] {
  const ta = translation(a);
  const w = apply(inverse(ta), b);
  const r = abs(w) || 1;
  const u = { re: w.re / r, im: w.im / r };
  const d = distanceFromOrigin(Math.min(r, 1 - 1e-15));
  const pts: Complex[] = [];
  for (let i = 0; i <= n; i++) {
    const s = -reach + ((d + 2 * reach) * i) / n;
    const rt = Math.tanh(s / 2);
    pts.push(apply(ta, { re: u.re * rt, im: u.im * rt }));
  }
  return pts;
}

/**
 * Interior angle at vertex v between the geodesics towards a and towards b, in [0, π].
 * Moving v to the origin with the translation τ_v⁻¹ (whose derivative at v is a positive real
 * number, so it does not rotate directions) turns both geodesics into diameters.
 */
export function angleAt(v: Complex, a: Complex, b: Complex): number {
  const tv = inverse(translation(v));
  const da = arg(apply(tv, a));
  const db = arg(apply(tv, b));
  let d = Math.abs(da - db);
  if (d > Math.PI) d = 2 * Math.PI - d;
  return d;
}

export function sampleCircle(c: Complex, r: number, n: number): Complex[] {
  const tc = translation(c);
  const rr = Math.tanh(r / 2);
  const pts: Complex[] = [];
  for (let i = 0; i <= n; i++) pts.push(apply(tc, fromPolar(rr, (2 * Math.PI * i) / n)));
  return pts;
}

export const circleCircumference = (r: number): number => 2 * Math.PI * Math.sinh(r);
export const circleArea = (r: number): number => 2 * Math.PI * (Math.cosh(r) - 1);

/* ------------------------------------------------------------------------------------------ *
 * Numerical integration (used to validate Gauss–Bonnet and the circumference law against the
 * metric itself, independently of the closed forms above).
 * ------------------------------------------------------------------------------------------ */

// 15-point Gauss–Kronrod rule with its embedded 7-point Gauss rule (nodes on [−1, 1]).
const XGK = [
  0.9914553711208126, 0.9491079123427585, 0.8648644233597691, 0.7415311855993945,
  0.5860872354676911, 0.4058451513773972, 0.20778495500789848, 0,
];
const WGK = [
  0.022935322010529224, 0.06309209262997856, 0.10479001032225019, 0.14065325971552592,
  0.1690047266392679, 0.19035057806478542, 0.20443294007529889, 0.20948214108472782,
];
const WG = [0.1294849661688697, 0.27970539148927664, 0.3818300505051189, 0.4179591836734694];

function gk15(f: (x: number) => number, a: number, b: number): [number, number] {
  const c = (a + b) / 2;
  const h = (b - a) / 2;
  const fc = f(c);
  let k = fc * WGK[7];
  let g = fc * WG[3];
  for (let j = 0; j < 7; j++) {
    const dx = h * XGK[j];
    const f1 = f(c - dx);
    const f2 = f(c + dx);
    k += WGK[j] * (f1 + f2);
    if (j % 2 === 1) g += WG[(j - 1) / 2] * (f1 + f2);
  }
  return [k * h, Math.abs((k - g) * h)];
}

/**
 * Globally adaptive Gauss–Kronrod quadrature of f on [a, b]: repeatedly bisects the interval
 * with the largest error estimate until the summed estimate is below max(tol, 1e-15·|I|).
 */
export function integrate(f: (x: number) => number, a: number, b: number, tol = 1e-13): number {
  type Piece = { lo: number; hi: number; val: number; err: number };
  const first = gk15(f, a, b);
  const pieces: Piece[] = [{ lo: a, hi: b, val: first[0], err: first[1] }];
  for (let iter = 0; iter < 4000; iter++) {
    let total = 0;
    let errSum = 0;
    let worst = 0;
    for (let i = 0; i < pieces.length; i++) {
      total += pieces[i].val;
      errSum += pieces[i].err;
      if (pieces[i].err > pieces[worst].err) worst = i;
    }
    if (errSum <= Math.max(tol, 1e-15 * Math.abs(total))) return total;
    const { lo, hi } = pieces[worst];
    const mid = (lo + hi) / 2;
    const left = gk15(f, lo, mid);
    const right = gk15(f, mid, hi);
    pieces[worst] = { lo, hi: mid, val: left[0], err: left[1] };
    pieces.push({ lo: mid, hi, val: right[0], err: right[1] });
  }
  return pieces.reduce((s, p) => s + p.val, 0);
}

/**
 * Area of the geodesic triangle abc by integrating the area element 4 dx dy / (1 − |z|²)²
 * in polar coordinates about a (moved to the origin). With R(θ) the Euclidean distance to the
 * opposite side along the ray at angle θ:  area = ∫ 2R² / (1 − R²) dθ.
 */
export function triangleAreaNumeric(a: Complex, b: Complex, c: Complex, tol = 1e-13): number {
  const ta = inverse(translation(a));
  const B = apply(ta, b);
  const C = apply(ta, c);
  const argB = arg(B);
  const argC = arg(C);
  let span = argC - argB;
  while (span > Math.PI) span -= 2 * Math.PI;
  while (span < -Math.PI) span += 2 * Math.PI;
  // integrate counter-clockwise from whichever ray comes first
  const tb = span < 0 ? argC : argB;
  span = Math.abs(span);
  // Geodesic BC: circle orthogonal to the unit circle with centre k (|k|² = 1 + ρ²), unless B,
  // C and the origin are collinear (then the triangle is degenerate with zero area).
  const Bb = { re: B.re, im: B.im };
  const Cc = { re: C.re, im: C.im };
  const det = Bb.re * Cc.im - Bb.im * Cc.re;
  if (Math.abs(det) < 1e-15) return 0;
  const rb = (1 + Bb.re * Bb.re + Bb.im * Bb.im) / 2;
  const rc = (1 + Cc.re * Cc.re + Cc.im * Cc.im) / 2;
  const kx = (rb * Cc.im - rc * Bb.im) / det;
  const ky = (Bb.re * rc - Cc.re * rb) / det;
  const radiusAt = (theta: number): number => {
    const m = kx * Math.cos(theta) + ky * Math.sin(theta);
    // r² − 2rm + 1 = 0, smaller root, written stably.
    return 1 / (m + Math.sqrt(Math.max(m * m - 1, 0)));
  };
  return integrate(
    (theta) => {
      const R = radiusAt(theta);
      return (2 * R * R) / (1 - R * R);
    },
    tb,
    tb + span,
    tol,
  );
}

/** Triangle angles (at a, b, c) and the Gauss–Bonnet area π − (α + β + γ). */
export function triangleMeasures(a: Complex, b: Complex, c: Complex) {
  const alpha = angleAt(a, b, c);
  const beta = angleAt(b, c, a);
  const gamma = angleAt(c, a, b);
  const sum = alpha + beta + gamma;
  return { alpha, beta, gamma, sum, area: Math.PI - sum };
}

/**
 * Length of the hyperbolic circle of radius r about c, measured by integrating the metric
 * along the curve z(φ) = τ_c(ρ e^{iφ}), ρ = tanh(r/2):  L = ∫ 2|z′(φ)| / (1 − |z(φ)|²) dφ.
 * The integrand is smooth and periodic, so the trapezoidal rule converges spectrally; the
 * number of nodes is doubled until two successive estimates agree.
 */
export function circleLengthNumeric(c: Complex, r: number): number {
  const rho = Math.tanh(r / 2);
  const c2 = c.re * c.re + c.im * c.im;
  const integrand = (phi: number): number => {
    const w = fromPolar(rho, phi);
    // z = (w + c)/(c̄ w + 1); z′ = dz/dw · dw/dφ, dz/dw = (1 − |c|²)/(c̄w + 1)², |dw/dφ| = ρ
    const dr = c.re * w.re + c.im * w.im + 1;
    const di = c.re * w.im - c.im * w.re;
    const d2 = dr * dr + di * di;
    const nr = w.re + c.re;
    const ni = w.im + c.im;
    const z2 = (nr * nr + ni * ni) / d2;
    const dzdw = (1 - c2) / d2;
    return (2 * dzdw * rho) / (1 - z2);
  };
  let n = 16;
  let prev = Number.NaN;
  for (;;) {
    let s = 0;
    for (let i = 0; i < n; i++) s += integrand((2 * Math.PI * i) / n);
    const est = (s * 2 * Math.PI) / n;
    if (Math.abs(est - prev) <= 1e-13 * Math.abs(est) || n > 1 << 16) return est;
    prev = est;
    n *= 2;
  }
}

/** Hyperbolic length of a polyline (sum of segment distances). */
export function polylineLength(points: Complex[]): number {
  let s = 0;
  for (let i = 1; i < points.length; i++) s += distance(points[i - 1], points[i]);
  return s;
}
